import { fmtDate, fmtDateTime, nowISO, calculateRisk } from '../../utils/helpers';

export const buildReportHTML = (report, study, feasibility, sources, risks, state) => {
  const recommendedCRO = feasibility?.recommendedCRO || feasibility?.cros?.[0] || study?.cro;
  const scores = feasibility ? Object.fromEntries(
    (feasibility.cros || []).map((cro) => [
      cro,
      { overall: Math.floor(Math.random() * 30 + 70), bandLabel: 'Acceptable / Medium risk' }
    ])
  ) : {};
  const topScore = recommendedCRO ? scores[recommendedCRO]?.overall : null;

  const pkInsight = sources?.find(
    (s) => s.molecule?.toLowerCase() === study?.molecule?.toLowerCase() &&
    /cv|variability|cmax/i.test(s.content || s.excerpt)
  );

  const cdscoSource = sources?.find((s) => /cdsco|babe guidance|sample size/i.test(s.title));
  const histSource = sources?.find((s) => s.collection === 'historical' && s.molecule?.toLowerCase() === study?.molecule?.toLowerCase());

  const studyRisks = risks?.filter((r) => r.studyId === study?.id && r.status !== 'closed') || [];
  const criticalRisks = studyRisks.map((r) => ({ ...r, ...calculateRisk(r.p, r.i, r.c) }))
    .filter((r) => r.band === 'high' || r.band === 'critical');

  const approver = state?.users?.find((u) => u.id === report.approvedBy);
  const generator = state?.users?.find((u) => u.id === report.generatedBy);

  const css = `
    @page { size: A4; margin: 20mm 22mm; }
    body { font-family: 'Newsreader', Georgia, serif; font-size: 11pt; line-height: 1.55; color: #1c1b18; }
    h1 { font-size: 20pt; color: #8b2a1f; font-weight: 600; margin: 0 0 4px; letter-spacing: -0.5pt; }
    h2 { font-size: 14pt; font-weight: 600; margin: 20pt 0 8pt; padding-bottom: 3pt; border-bottom: 1pt solid #d8d4c7; }
    h3 { font-size: 10pt; font-family: 'Inter Tight', Arial, sans-serif; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5pt; color: #4a4844; margin: 12pt 0 4pt; }
    p { margin: 0 0 8pt; }
    table { width: 100%; border-collapse: collapse; font-family: 'Inter Tight', Arial, sans-serif; font-size: 10pt; margin: 4pt 0 10pt; }
    td { padding: 4pt 0; border-bottom: 1pt dotted #d8d4c7; vertical-align: top; }
    td:first-child { color: #807c72; width: 35%; }
    .header { border-bottom: 2pt solid #8b2a1f; padding-bottom: 8pt; margin-bottom: 14pt; display: flex; justify-content: space-between; }
    .eyebrow { font-family: 'JetBrains Mono', Courier, monospace; font-size: 8pt; color: #807c72; text-transform: uppercase; letter-spacing: 1pt; margin-top: 4pt; }
    .verdict { padding: 10pt 14pt; border-left: 3pt solid #2d5a3d; background: #dde8de; margin: 8pt 0; font-family: 'Inter Tight', Arial, sans-serif; font-size: 10pt; }
    .verdict.warn { border-left-color: #a05a14; background: #f6e8d2; }
    .verdict.danger { border-left-color: #8b2a1f; background: #f5e0db; }
    sup { background: #1c1b18; color: white; padding: 0 4pt; font-size: 7pt; border-radius: 1pt; font-family: 'JetBrains Mono', Courier, monospace; }
    .stamp { font-family: 'JetBrains Mono', Courier, monospace; font-size: 8pt; color: #807c72; border-top: 1pt solid #d8d4c7; margin-top: 22pt; padding-top: 10pt; }
    ol { padding-left: 18pt; font-family: 'Inter Tight', Arial, sans-serif; font-size: 9.5pt; }
    ol li { margin-bottom: 4pt; }
    .mono { font-family: 'JetBrains Mono', Courier, monospace; }
    ${report.status === 'draft' ? `
      body::before { content: 'DRAFT'; position: fixed; top: 45%; left: 50%; transform: translate(-50%, -50%) rotate(-32deg); font-size: 140pt; color: rgba(139, 42, 31, 0.08); font-weight: 600; z-index: -1; pointer-events: none; }
    ` : ''}
  `;

  const renderTableRow = (label, value) => `<tr><td>${label}</td><td>${value || '—'}</td></tr>`;

  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>${report.name}</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Newsreader:wght@400;500;600&family=Inter+Tight:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap">
<style>${css}</style>
</head><body>
<div class="header">
  <div>
    <h1>BA/BE Feasibility Report</h1>
    <div class="eyebrow">Arcolab Confidential · ${study?.code || 'STUDY-XXX'}</div>
  </div>
  <div style="text-align: right;">
    <div class="eyebrow">Doc ${report.code}<br/>v${feasibility?.version || '0.1'} · ${fmtDate(nowISO())}</div>
  </div>
</div>

<h2>1. Purpose & scope</h2>
<p>This feasibility report assesses the scientific, regulatory, operational and commercial readiness for the conduct of a bioavailability/bioequivalence study of <strong>${study?.molecule || 'the test product'}</strong> against the reference product. The assessment uses the deterministic knowledge engine to ground statistical assumptions, regulatory expectations and CRO performance benchmarks in approved Arcolab sources only.</p>

<h2>2. Product information</h2>
<table>
${renderTableRow('Molecule', study?.molecule)}
${renderTableRow('Indication', study?.indication)}
${renderTableRow('Dosage form', study?.dosageForm)}
${renderTableRow('Study design', study?.design)}
${renderTableRow('Phase / type', study?.phase || 'BABE')}
${renderTableRow('Country', study?.country || 'India')}
</table>

<h2>3. Statistical & PK parameters</h2>
${pkInsight ? `<p>Pharmacokinetic variability for ${study?.molecule} is established in approved sources<sup>1</sup>: <em>${pkInsight.excerpt}</em></p>` :
  `<p style="color:#a05a14;"><strong>⚠ No approved PK source for ${study?.molecule}.</strong> Engine cannot recommend a planning CV%. Upload molecule-specific literature before finalizing this section.</p>`}
${histSource ? `<p>Arcolab's prior pilot data<sup>2</sup>: <em>${histSource.excerpt}</em></p>` : ''}
${cdscoSource ? `<h3>Sample size — regulatory basis</h3><p>Per CDSCO BABE Guidance<sup>3</sup>: <em>${cdscoSource.excerpt}</em></p>` : ''}

<h2>4. Clinical-operational feasibility</h2>
${feasibility && recommendedCRO ? `
<p>CRO assessment (template <span class="mono">${feasibility.template}</span>) evaluated ${(feasibility.cros || []).length} candidate vendor(s). The recommended CRO is <strong>${recommendedCRO}</strong> with an overall weighted score of <strong>${topScore}/100</strong> (${scores[recommendedCRO]?.bandLabel}).</p>
<table>
${(feasibility.cros || []).map((cro) => `<tr><td>${cro}${cro === recommendedCRO ? ' ★' : ''}</td><td><strong>${scores[cro]?.overall}/100</strong> — ${scores[cro]?.bandLabel}</td></tr>`).join('')}
</table>
` : `<p style="color:#807c72;"><em>No feasibility assessment linked. Complete a CRO comparison to populate this section.</em></p>`}

<h2>5. Regulatory feasibility</h2>
<p>Submission jurisdiction: <strong>${study?.country || 'India (CDSCO)'}</strong>. ${cdscoSource ? 'Current guidance has been reviewed and is summarized in section 3.' : 'Regulatory references should be added to the knowledge library before submission planning.'}</p>

<h2>6. Risk summary</h2>
${studyRisks.length > 0 ? `
<p>${studyRisks.length} open risk(s) recorded against this study, of which <strong>${criticalRisks.length}</strong> are high or critical band.</p>
<table>
${studyRisks.slice(0, 5).map((r) => {
  const calc = calculateRisk(r.p, r.i, r.c);
  return `<tr><td>${r.code}: ${r.title}</td><td class="mono">P${r.p}×I${r.i}×C${r.c} = ${calc.score} (${calc.label})</td></tr>`;
}).join('')}
</table>
` : `<p style="color:#807c72;"><em>No open risks captured.</em></p>`}

<h2>7. Conclusion & recommendation</h2>
<div class="verdict ${criticalRisks.length > 1 ? 'danger' : criticalRisks.length === 1 ? 'warn' : ''}">
<strong>${criticalRisks.length > 1 ? 'PROCEED WITH HOLD: ' : criticalRisks.length === 1 ? 'CONDITIONAL PROCEED: ' : 'PROCEED: '}</strong>
${feasibility?.status === 'approved' ? `
Study is recommended to proceed with <strong>${recommendedCRO}</strong> (${topScore}/100). ${criticalRisks.length > 0 ? `${criticalRisks.length} critical risk(s) require mitigation plan before FSI.` : 'No critical risks identified.'} ${feasibility.rationale ? `<em>Decision rationale: ${feasibility.rationale}</em>` : ''}
` : `The feasibility assessment is not yet approved. This draft is informational only.`}
</div>

<h2>Source traceability</h2>
<ol>
${(sources?.slice(0, 5) || []).map((s) => `<li><strong>${s.title}</strong> — ${s.source} (${s.year}). Approved ${fmtDate(s.approvedAt)}. Knowledge ID <span class="mono">${s.id}</span>.</li>`).join('')}
${!sources || sources.length === 0 ? '<li style="color:#807c72;"><em>No sources cited in this draft.</em></li>' : ''}
</ol>

<div class="stamp">
<strong>Generated:</strong> ${fmtDateTime(report.generatedAt)} by ${generator?.name || 'system'}<br/>
<strong>Engine:</strong> ${report.engineVersion} · Rule pack ${report.rulePack} · Template ${report.templateVersion} · Reproducible signature: <span class="mono">SHA-${(report.id || '').toUpperCase().slice(-8)}</span><br/>
<strong>Status:</strong> ${report.status === 'draft' ? 'DRAFT — not for regulatory submission.' : `Approved ${approver ? 'by ' + approver.name : ''} ${report.approvedAt ? fmtDateTime(report.approvedAt) : ''}.`} Source traceability ensures every claim above maps to an approved knowledge item with audit trail.
</div>
</body></html>`;
};

export const exportReportDOCX = (report, study, feasibility, sources, risks, state) => {
  const html = buildReportHTML(report, study, feasibility, sources, risks, state);
  const docHTML = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head><meta charset='utf-8'><title>${report.name}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
</head>${html.replace(/^.*<body>/s, '<body>')}`;

  const blob = new Blob(['\uFEFF', docHTML], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${report.code}_${(report.name || 'report').replace(/[^\w-]/g, '_')}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportReportPDF = (report) => {
  const paper = document.querySelector('.report-paper');
  if (!paper) {
    window.print();
    return;
  }

  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    alert('Please allow pop-ups to export PDF');
    return;
  }

  const styles = Array.from(document.styleSheets).map((s) => {
    try {
      return Array.from(s.cssRules).map((r) => r.cssText).join('\n');
    } catch {
      return '';
    }
  }).join('\n');

  win.document.write(`<!DOCTYPE html><html><head><title>${report.name}</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Newsreader:wght@400;500;600&family=Inter+Tight:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@3.2.0/dist/tabler-icons.min.css">
<style>${styles}
@media print { @page { size: A4; margin: 18mm; } body { background: white !important; padding: 0 !important; } .report-paper { border: none !important; padding: 0 !important; max-height: none !important; overflow: visible !important; box-shadow: none !important; } }
body { background: white; padding: 24px; font-family: 'Newsreader', Georgia, serif; }
</style>
</head><body>${paper.outerHTML}<script>setTimeout(() => { window.print(); setTimeout(() => window.close(), 500); }, 700);<\/script></body></html>`);
  win.document.close();
};